import React, { useState } from 'react';
import {
  Box, Card, CardContent, FormControlLabel, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import type { Year } from '../../services/household-retirement-service';
import { compact } from './format';

const COLS: { key: keyof Year; label: string; title?: string }[] = [
  { key: 'employment', label: 'Salary', title: 'Employment income incl. employer top-up' },
  { key: 'ei', label: 'EI' },
  { key: 'cpp', label: 'CPP' },
  { key: 'oas', label: 'OAS' },
  { key: 'tax', label: 'Tax', title: 'Estimated income tax, CPP/EI premiums and OAS recovery' },
  { key: 'after_tax_income', label: 'After tax' },
  { key: 'core_spending', label: 'Core spend' },
  { key: 'child_costs', label: 'Kids', title: 'Childcare and activities' },
  { key: 'education', label: 'Education', title: 'Post-secondary not covered by the RESP' },
  { key: 'debt_payments', label: 'Debt pmts' },
  { key: 'contributions', label: '→ Portfolio' },
  { key: 'withdrawals', label: 'Portfolio →' },
  { key: 'investable', label: 'Investable' },
  { key: 'home_equity', label: 'Home equity' },
  { key: 'debt', label: 'Debt' },
  { key: 'net_worth', label: 'Net worth' },
];

/**
 * Year-by-year cash flow of the deterministic projection. Every fifth year
 * by default (the whole table on demand); nominal dollars, compact.
 */
export const CashFlowTable: React.FC<{ years: Year[] }> = ({ years }) => {
  const [all, setAll] = useState(false);
  const first = years[0]?.year;
  const shown = all ? years : years.filter((y) => y.year === first || y.year % 5 === 0 || y.phase !== 'working' && y.year <= first + 2);
  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">Cash flow — everything goes well (future dollars)</Typography>
          <FormControlLabel control={<Switch size="small" checked={all} onChange={(e) => setAll(e.target.checked)} />}
            label="Every year" />
        </Stack>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader aria-label="Cash flow table">
            <TableHead>
              <TableRow>
                <TableCell>Year</TableCell>
                <TableCell>Phase</TableCell>
                {COLS.map((c) => <TableCell key={c.key} align="right" title={c.title}>{c.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((y) => (
                <TableRow key={y.year} hover data-testid={`cf-${y.year}`}>
                  <TableCell>
                    {y.year}
                    <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
                      {Object.entries(y.ages).filter(([, a]) => a >= 0).map(([n, a]) => `${n} ${Math.floor(a)}`).join(' · ')}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{y.phase}</TableCell>
                  {COLS.map((c) => (
                    <TableCell key={c.key} align="right" className="fw-num">
                      {(y[c.key] as number) ? compact(y[c.key] as number) : '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};
